import { Suspense, lazy, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { X, Download } from 'lucide-react'
import {
  fetchConversationSpreadsheets,
  postSpreadsheetVersion,
  renameSpreadsheet,
  spreadsheetDownloadUrl,
  spreadsheetSnapshotUrl,
  StaleBaseVersionError,
  type ApiConvSpreadsheet,
  type SpreadsheetEditOp,
} from '../../lib/chatApi'
import { EditableTitle } from './EditableTitle'
import { VersionPicker } from './VersionPicker'
import { useChatStore } from '../../stores/chatStore'
import { OverrideOlderVersionModal } from './spreadsheet/OverrideOlderVersionModal'
import type { ConversationSearch } from './conversationSearch'
import { useActiveWorkspace } from '../../lib/workspace'

const ADVANCED_LABELS: Record<string, string> = {
  charts: 'charts',
  images: 'images',
  pivots: 'pivot tables',
  macros: 'macros',
}

function formatKindList(kinds: string[]): string {
  const labels = kinds.map(k => ADVANCED_LABELS[k] ?? k)
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

const UniverGrid = lazy(() => import('./UniverGrid'))

export interface SpreadsheetSidePanelProps {
  conversationId: string
  artifactId: string
  versionNum: number | null
  onVersionChange: (versionNum: number) => void
  onClose: () => void
}

function PanelSkeleton() {
  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
        <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
      </div>
      <div className="flex-1 bg-gray-50/40 animate-pulse" />
    </div>
  )
}

function EmptyPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
        <div className="text-sm font-semibold text-gray-600">Spreadsheet</div>
        <button
          onClick={onClose}
          aria-label="Close spreadsheet panel"
          className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
        >
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center text-sm text-gray-500 px-6 text-center">
        The spreadsheet is no longer available in this conversation.
      </div>
    </div>
  )
}

export function SpreadsheetSidePanel({
  conversationId,
  artifactId,
  versionNum,
  onVersionChange,
  onClose,
}: SpreadsheetSidePanelProps) {
  // isStreaming is the canonical "agent turn in flight" signal — lit
  // from status:thinking, cleared at done. During this window the
  // entire panel goes read-only to keep agent and user writes
  // mutually exclusive.
  const isStreaming = useChatStore((s) => s.isStreaming)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { activeWorkspaceKey } = useActiveWorkspace()

  const { data: spreadsheets, isLoading } = useQuery({
    queryKey: ['conversationSpreadsheets', activeWorkspaceKey, conversationId],
    queryFn: () => fetchConversationSpreadsheets(activeWorkspaceKey, conversationId),
    enabled: !!activeWorkspaceKey && !!conversationId,
    refetchOnWindowFocus: false,
  })

  const versions = useMemo(() => {
    if (!spreadsheets) return [] as ApiConvSpreadsheet[]
    return spreadsheets
      .filter((s) => s.artifact_id === artifactId)
      .sort((a, b) => a.version_num - b.version_num)
  }, [spreadsheets, artifactId])

  // Op buffer. Cell ops collapse by (sheet,row,col) — last-write-wins —
  // so repeated edits to the same cell stay as one row. Style / merge /
  // unmerge ops are appended in order; the backend applies them
  // sequentially and cheaply tolerates duplicates.
  const [ops, setOps] = useState<SpreadsheetEditOp[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [advancedNotice, setAdvancedNotice] = useState<string[] | null>(null)

  if (isLoading) return <PanelSkeleton />
  if (versions.length === 0) return <EmptyPanel onClose={onClose} />

  const latestVersionNum = versions[versions.length - 1].version_num
  const targetVersion = versionNum ?? latestVersionNum
  const active =
    versions.find((v) => v.version_num === targetVersion)
    ?? versions[versions.length - 1]

  const versionPickerItems = versions.map((v) => ({
    id: v.id,
    versionNum: v.version_num,
    createdAt: v.created_at,
  }))

  const isLocked = isStreaming || saving
  const isDirty = ops.length > 0
  const canSave = !isLocked && isDirty
  const isViewingLatest = active.version_num === latestVersionNum

  const handleCellEdit = (incoming: SpreadsheetEditOp[]) => {
    if (incoming.length === 0) return
    setOps((prev) => {
      const next = [...prev]
      for (const op of incoming) {
        if (op.kind === 'cell') {
          // last-write-wins collapse by (sheet,row,col)
          const idx = next.findIndex(
            (o) =>
              o.kind === 'cell' &&
              o.sheet === op.sheet &&
              o.row === op.row &&
              o.col === op.col,
          )
          if (idx >= 0) next[idx] = op
          else next.push(op)
        } else {
          next.push(op)
        }
      }
      return next
    })
  }

  const handleAdvancedElements = (kinds: string[]) => {
    if (kinds.length === 0) return
    // Dismiss memo per artifact so switching versions doesn't re-fire the banner.
    const key = `spreadsheet-adv-dismissed-${artifactId}`
    try {
      if (sessionStorage.getItem(key)) return
    } catch {
      // sessionStorage may be unavailable (private mode); fall through and show.
    }
    setAdvancedNotice(kinds)
  }

  const dismissAdvancedNotice = () => {
    try {
      sessionStorage.setItem(`spreadsheet-adv-dismissed-${artifactId}`, '1')
    } catch {
      /* best-effort */
    }
    setAdvancedNotice(null)
  }

  const performSave = async (force: boolean) => {
    setSaving(true)
    setSaveError(null)
    try {
      const result = await postSpreadsheetVersion(activeWorkspaceKey, artifactId, {
        base_version: active.version_num,
        ops,
        force,
      })
      setOps([])
      // Refresh the versions list before navigating so `active` points at
      // the new row — otherwise the next save sends a stale base_version.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversationSpreadsheets', activeWorkspaceKey, conversationId] }),
        queryClient.invalidateQueries({ queryKey: ['allSpreadsheets', activeWorkspaceKey] }),
      ])
      void navigate({
        to: '/assistant/$conversationId',
        params: { conversationId },
        search: (prev: ConversationSearch) => ({
          ...prev,
          sheet: artifactId,
          v: result.version_num,
        }),
      })
    } catch (err) {
      if (err instanceof StaleBaseVersionError) {
        setSaveError(
          "Someone just saved a newer version. Close and reopen the editor to see it.",
        )
      } else {
        setSaveError("Couldn't save your changes. Try again in a moment.")
      }
    } finally {
      setSaving(false)
      setShowOverrideModal(false)
    }
  }

  const handleSave = () => {
    if (!canSave) return
    if (!isViewingLatest) {
      setShowOverrideModal(true)
      return
    }
    void performSave(false)
  }

  const patchTitleCaches = (title: string) => {
    type Row = { id: string; title: string; artifact_id?: string }
    const patch = (old: Row[] | undefined) =>
      old?.map((s) => (s.artifact_id === artifactId ? { ...s, title } : s))
    queryClient.setQueriesData<Row[] | undefined>({ queryKey: ['conversationSpreadsheets'] }, patch)
    queryClient.setQueriesData<Row[] | undefined>({ queryKey: ['allSpreadsheets'] }, patch)
  }

  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 gap-2">
        <div className="text-sm font-semibold text-gray-900 truncate min-w-0 flex items-center gap-2">
          <EditableTitle
            value={active.title}
            canEdit={!isLocked}
            trigger="doubleClick"
            layout="inline"
            className="truncate"
            inputClassName="w-full bg-white border border-gray-300 rounded px-1.5 py-0.5 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-slate-400 text-center"
            ariaLabel="Rename spreadsheet"
            onSave={async (next) => {
              const prev = active.title
              patchTitleCaches(next)
              try {
                await renameSpreadsheet(activeWorkspaceKey, active.artifact_id, next)
              } catch (e) {
                patchTitleCaches(prev)
                throw e
              }
            }}
          />
          {isDirty && !saving && (
            <span
              className="inline-flex items-center shrink-0 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded"
              title="Unsaved changes"
            >
              Unsaved
            </span>
          )}
          {saving && (
            <span className="inline-flex items-center shrink-0 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded">
              Saving&hellip;
            </span>
          )}
          {isStreaming && (
            <span className="inline-flex items-center shrink-0 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 bg-white border border-slate-200 rounded">
              Agent is working&hellip;
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {versions.length > 1 && (
            <VersionPicker
              versions={versionPickerItems}
              value={active.version_num}
              onChange={onVersionChange}
              ariaLabel="Spreadsheet version"
              buttonClassName="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-md transition-colors"
            />
          )}
          <a
            href={spreadsheetDownloadUrl(activeWorkspaceKey, active.id)}
            download={`${active.title || 'spreadsheet'}.xlsx`}
            data-testid="spreadsheet-xlsx-download"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-md transition-colors"
            aria-label="Download as XLSX"
          >
            <Download size={12} />
            XLSX
          </a>
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
            title={
              isStreaming
                ? 'Agent is working'
                : saving
                  ? 'Saving'
                  : isDirty
                    ? 'Save changes'
                    : 'No changes to save'
            }
          >
            Save
          </button>
          <button
            onClick={onClose}
            aria-label="Close spreadsheet panel"
            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded ml-1"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      {saveError && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
          <span>{saveError}</span>
          <button
            type="button"
            onClick={() => setSaveError(null)}
            className="text-amber-700 hover:text-amber-900"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      )}
      {advancedNotice && (
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-700 flex items-center justify-between gap-2">
          <span>
            This workbook contains {formatKindList(advancedNotice)} that aren’t
            shown in the editor. They’re preserved when you download.
          </span>
          <button
            type="button"
            onClick={dismissAdvancedNotice}
            className="text-slate-500 hover:text-slate-800"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div className="flex-1 overflow-hidden">
        <Suspense fallback={<PanelSkeleton />}>
          <UniverGrid
            snapshotUrl={spreadsheetSnapshotUrl(activeWorkspaceKey, active.id)}
            readOnly={isLocked}
            onCellEdit={handleCellEdit}
            onAdvancedElements={handleAdvancedElements}
          />
        </Suspense>
      </div>
      {showOverrideModal && (
        <OverrideOlderVersionModal
          viewingVersion={active.version_num}
          latestVersion={latestVersionNum}
          onConfirm={() => void performSave(true)}
          onCancel={() => setShowOverrideModal(false)}
        />
      )}
    </div>
  )
}
